import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, MailIcon } from '../components/Icons';
import * as authService from '../services/api/auth';
import './VerifyOtp.css';

const VerifyOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    if (!location.state?.email) {
      setInfo('Enter your email and the OTP we sent to continue.');
    }
  }, [location.state?.email]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    if (!/^\d{6}$/.test(otp.trim())) {
      setError('OTP must be exactly 6 digits.');
      return;
    }

    setLoading(true);
    try {
      const result = await authService.verifyResetOtp(email.trim(), otp.trim());
      if (result.success) {
        navigate('/reset-password', { state: { email: email.trim(), otp: otp.trim() } });
      } else {
        setError(result.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');

    if (!email.trim()) {
      setError('Enter your email address first to resend OTP.');
      return;
    }

    setResending(true);
    try {
      const result = await authService.forgotPassword(email.trim());
      if (result.success) {
        setInfo('A new OTP has been sent to your email.');
      } else {
        setError(result.message || 'Unable to resend OTP right now.');
      }
    } catch (err) {
      setError(err.message || 'Unable to resend OTP right now.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="verify-otp-screen">
      <div className="verify-otp-container">
        <div className="verify-otp-card">
          <div className="verify-otp-header">
            <button className="verify-otp-back" onClick={() => navigate('/forgot-password')} aria-label="Back">
              <ArrowLeftIcon size={20} color="currentColor" />
            </button>
            <h1 className="verify-otp-header-title">Verify OTP</h1>
            <div className="verify-otp-spacer" />
          </div>

          <div className="verify-otp-content">
            <div className="verify-otp-icon" aria-hidden="true">
              <MailIcon size={52} color="#2563eb" />
            </div>
            <h2 className="verify-otp-title">Enter Verification Code</h2>
            <p className="verify-otp-subtitle">
              Enter the 6-digit code sent to your email to continue resetting your password.
            </p>

            <form onSubmit={handleVerify} className="verify-otp-form">
              {error && <div className="verify-otp-error">{error}</div>}
              {info && <div className="verify-otp-info">{info}</div>}

              <div className="verify-otp-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  className="verify-otp-input"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoCapitalize="none"
                  required
                />
              </div>

              <div className="verify-otp-group">
                <label htmlFor="otp">One-Time Password (OTP)</label>
                <input
                  type="text"
                  id="otp"
                  className="verify-otp-input verify-otp-code"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  required
                />
              </div>

              <button type="submit" className="btn-primary verify-otp-submit" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify OTP'}
                {!loading && <ArrowRightIcon size={18} color="#ffffff" />}
              </button>
            </form>

            <button type="button" className="verify-otp-resend" onClick={handleResend} disabled={resending}>
              {resending ? 'Resending OTP...' : 'Resend OTP'}
            </button>

            <div className="verify-otp-login-link">
              <Link to="/login" className="verify-otp-back-link">
                <ArrowLeftIcon size={16} color="currentColor" />
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
